import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Icon } from '@iconify/react';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { selectIsGuest } from '@features/auth';
import {
  useGetProjectsQuery,
  useCreateProjectMutation,
  selectCurrentProject,
  setCurrentProject,
} from '@features/projects';

// Below this a search box is more noise than help; above it, scanning the
// list stops being practical.
const SEARCH_THRESHOLD = 8;

export function ProjectSelector({ variant = 'default' }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentProject = useAppSelector(selectCurrentProject);
  const isGuest = useAppSelector(selectIsGuest);
  const { data: projects = [] } = useGetProjectsQuery();
  const [createProject] = useCreateProjectMutation();
  const activeProjects = useMemo(() => projects.filter((project) => project.status === 'active'), [projects]);

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const activeItemRef = useRef(null);

  // Displayed alphabetically so a long list can be scanned. The
  // workspace restores the most recent project independently of this order.
  const sortedProjects = useMemo(
    () => [...activeProjects].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [activeProjects],
  );
  const visibleProjects = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return sortedProjects;
    return sortedProjects.filter((project) => project.name.toLowerCase().includes(term));
  }, [sortedProjects, query]);
  const showSearch = activeProjects.length >= SEARCH_THRESHOLD;

  const closeCreateModal = useCallback(() => {
    setIsCreating(false);
    setNewName('');
    setCreateError(null);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsCreating(false);
        setNewName('');
      }
    };
    if (isOpen) {
      globalThis.document.addEventListener('mousedown', handleClickOutside);
      return () => globalThis.document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Auto-focus input when creating
  useEffect(() => {
    if (isCreating) inputRef.current?.focus();
  }, [isCreating]);

  // A stale filter would make the list look empty the next time it opens.
  useEffect(() => {
    if (!isOpen) setQuery('');
    else if (showSearch) searchRef.current?.focus();
  }, [isOpen, showSearch]);

  // Typing narrows the list, so the highlight goes back to the top match:
  // that makes "type a few letters, press Enter" the fast path.
  useEffect(() => setActiveIndex(0), [query, isOpen]);

  // Keep the highlighted row inside the scroll cap.
  useEffect(() => {
    if (isOpen) activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const handleSelect = useCallback((project) => {
    dispatch(setCurrentProject(project));
    setIsOpen(false);
  }, [dispatch]);

  // Bound to the wrapper rather than the search input so the arrows also work
  // on short lists, where there is no search box and focus stays on the trigger.
  const handleKeyDown = useCallback((event) => {
    if (!isOpen || visibleProjects.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => (i + step + visibleProjects.length) % visibleProjects.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const project = visibleProjects[activeIndex];
      if (project) handleSelect(project);
    }
  }, [isOpen, visibleProjects, activeIndex, handleSelect]);

  const [createError, setCreateError] = useState(null);

  const handleCreate = useCallback(async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      setCreateError(null);
      const result = await createProject({ name: newName.trim() }).unwrap();
      dispatch(setCurrentProject(result));
      setNewName('');
      setIsCreating(false);
      setIsOpen(false);
    } catch {
      setCreateError(t('workspace.projectSelector.createFailed'));
    }
  }, [newName, createProject, dispatch, t]);

  const isSidebar = variant === 'sidebar';
  const isHeader = variant === 'header';
  const isFirstRun = variant === 'first-run' && activeProjects.length === 0;
  const isEmptySidebar = isSidebar && activeProjects.length === 0;

  // A guest cannot create projects, so the "Analyze your first company" call
  // to action would dead-end; the layout shows its own guest empty state.
  if (isGuest && (isFirstRun || isEmptySidebar)) return null;

  return (
    <div className="relative" ref={dropdownRef} data-tour="project-selector" onKeyDown={handleKeyDown}>
      <button
        type="button"
        onClick={() => {
          if (isFirstRun || isEmptySidebar) {
            setIsCreating(true);
          } else {
            setIsOpen(!isOpen);
          }
        }}
        className={`min-w-0 cursor-pointer flex items-center gap-2 rounded-lg transition-colors ${
          isSidebar
            ? 'w-full px-3 py-2.5 bg-[var(--color-bg-muted)] hover:bg-[var(--color-border)] border border-[var(--color-border)]'
            : isFirstRun
              ? 'w-full justify-center bg-[var(--color-accent)] px-5 py-3 text-white shadow-sm hover:bg-[var(--color-accent-hover)]'
              : isHeader
                ? 'max-w-56 border border-transparent px-2.5 py-1.5 hover:border-[var(--color-border)] hover:bg-[var(--color-bg-muted)] sm:max-w-72'
                : 'px-3 py-1.5 hover:bg-[var(--color-bg-muted)]'
        }`}
      >
        <Icon icon={isFirstRun ? 'mdi:sparkles' : 'mdi:briefcase-outline'} className={`${isSidebar ? 'size-5' : 'size-4'} ${isFirstRun ? 'text-white' : 'text-[var(--color-accent)]'} shrink-0`} />
        <span className={`font-semibold truncate ${isFirstRun ? 'text-white' : 'flex-1 text-left text-[var(--color-text)]'} ${isSidebar ? 'text-base' : 'text-sm'}`}>
          {isFirstRun || isEmptySidebar ? t('workspace.projectSelector.firstCompany') : currentProject ? currentProject.name : t('workspace.projectSelector.choose')}
        </span>
        {!isFirstRun && !isEmptySidebar && <Icon icon="mdi:chevron-down" className={`size-4 text-[var(--color-text-muted)] transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />}
      </button>

      {isOpen && (
        <div className={`absolute top-full mt-1 w-72 bg-white border border-[var(--color-border)] rounded-lg shadow-lg z-50 ${
          isSidebar ? 'left-0 right-0 w-auto' : isHeader ? 'right-0' : 'left-0'
        }`}>
          {activeProjects.length > 0 && (
            <div>
              {showSearch && (
                <div className="border-b border-[var(--color-border)] p-2">
                  <div className="relative">
                    <Icon icon="mdi:magnify" className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
                    <input
                      ref={searchRef}
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key !== 'Escape') return;
                        event.stopPropagation();
                        // First Escape clears the filter, a second one closes.
                        if (query) setQuery('');
                        else setIsOpen(false);
                      }}
                      role="combobox"
                      aria-expanded="true"
                      aria-controls="project-selector-list"
                      aria-activedescendant={visibleProjects[activeIndex] ? `project-option-${visibleProjects[activeIndex].uuid}` : undefined}
                      placeholder={t('workspace.projectSelector.searchPlaceholder')}
                      aria-label={t('workspace.projectSelector.searchLabel')}
                      className="w-full rounded-md border border-[var(--color-border)] py-1.5 pl-8 pr-2 text-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                    />
                  </div>
                </div>
              )}
              <p className="px-3 py-1.5 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">{t('workspace.projectSelector.projects')}</p>
              <div className="max-h-72 overflow-y-auto" id="project-selector-list" role="listbox">
                {visibleProjects.map((project, index) => (
                  <button
                    key={project.uuid}
                    id={`project-option-${project.uuid}`}
                    ref={index === activeIndex ? activeItemRef : null}
                    role="option"
                    aria-selected={currentProject?.uuid === project.uuid}
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => handleSelect(project)}
                    className={`cursor-pointer w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      index === activeIndex ? 'bg-[var(--color-bg-muted)]' : ''
                    } ${
                      currentProject?.uuid === project.uuid ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] font-medium' : 'text-[var(--color-text)]'
                    }`}
                  >
                    <Icon icon="mdi:briefcase-outline" className="size-4 shrink-0" />
                    <span className="truncate">{project.name}</span>
                  </button>
                ))}
                {visibleProjects.length === 0 && (
                  <p className="px-3 py-4 text-center text-sm text-[var(--color-text-muted)]">{t('workspace.projectSelector.noMatch', { query: query.trim() })}</p>
                )}
              </div>
            </div>
          )}

          {activeProjects.length === 0 && (
            <div className="px-4 py-5 text-center">
              <Icon icon="mdi:briefcase-plus-outline" className="mx-auto size-7 text-[var(--color-text-muted)]" />
              <p className="mt-2 text-sm font-medium text-[var(--color-text)]">{isGuest ? t('workspace.projectSelector.noSharedReports') : t('workspace.projectSelector.noProjects')}</p>
              {!isGuest && <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">{t('workspace.projectSelector.noProjectsHint')}</p>}
            </div>
          )}

          {/* Create new project — hidden for guests, who are view-only */}
          {!isGuest && (
            <div className="border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={() => { setIsOpen(false); setIsCreating(true); }}
                className="cursor-pointer w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-accent)] hover:bg-[var(--color-bg-muted)] transition-colors"
              >
                <Icon icon="mdi:plus" className="size-4" />
                {t('workspace.projectSelector.evaluate')}
              </button>
            </div>
          )}
        </div>
      )}

      {isCreating && (
        <div
          className="fixed inset-0 z-80 flex items-start justify-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px] md:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-project-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeCreateModal();
          }}
        >
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-[var(--color-border)] bg-white shadow-2xl">
            <div className="grid md:grid-cols-[1.05fr_0.95fr]">
              <form onSubmit={handleCreate} className="flex flex-col p-7 sm:p-9">
                <div className="mb-8 flex items-start justify-between gap-4">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">
                      {t('workspace.projectSelector.create.eyebrow')}
                    </p>
                    <h2 id="create-project-title" className="text-2xl font-semibold text-[var(--color-text)]">
                      {t('workspace.projectSelector.create.title')}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]">
                      {t('workspace.projectSelector.create.subtitle')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    aria-label={t('workspace.projectSelector.create.close')}
                    className="cursor-pointer flex size-9 shrink-0 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-muted)]"
                  >
                    <Icon icon="mdi:close" className="size-5" />
                  </button>
                </div>

                <label htmlFor="new-company-name" className="mb-2 text-sm font-medium text-[var(--color-text)]">
                  {t('workspace.projectSelector.create.companyName')}
                </label>
                <input
                  ref={inputRef}
                  id="new-company-name"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('workspace.projectSelector.create.companyPlaceholder')}
                  aria-label={t('workspace.projectSelector.create.companyName')}
                  className="w-full rounded-xl border border-[var(--color-border)] px-4 py-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') closeCreateModal();
                  }}
                />
                {createError && <p className="mt-2 text-sm text-red-600">{createError}</p>}

                <div className="mt-auto flex justify-end gap-3 pt-9">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    className="cursor-pointer rounded-lg border border-[var(--color-border)] px-4 py-2.5 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]"
                  >
                    {t('workspace.projectSelector.create.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={!newName.trim()}
                    className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon icon="mdi:sparkles" className="size-4" />
                    {t('workspace.projectSelector.create.submit')}
                  </button>
                </div>
              </form>

              <aside className="bg-[#184774] p-7 text-white sm:p-9">
                <div className="mb-6 flex size-11 items-center justify-center rounded-xl bg-white/12">
                  <Icon icon="mdi:file-chart-outline" className="size-6" />
                </div>
                <h3 className="text-xl font-semibold">{t('workspace.projectSelector.create.asideTitle')}</h3>
                <p className="mt-3 text-sm leading-6 text-blue-100">
                  {t('workspace.projectSelector.create.asideBody')}
                </p>
                <div className="mt-7 space-y-5">
                  {[
                    ['mdi:web', 'workspace.projectSelector.create.stepResearch'],
                    ['mdi:chart-box-outline', 'workspace.projectSelector.create.stepGenerate'],
                    ['mdi:folder-search-outline', 'workspace.projectSelector.create.stepOrganize'],
                  ].map(([icon, textKey]) => (
                    <div key={textKey} className="flex items-start gap-3">
                      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-white/12">
                        <Icon icon={icon} className="size-4" />
                      </span>
                      <span className="text-sm leading-6 text-blue-50">{t(textKey)}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-8 border-t border-white/15 pt-5 text-xs leading-5 text-blue-200">
                  {t('workspace.projectSelector.create.asideFooter')}
                </p>
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ProjectSelector.propTypes = {
  variant: PropTypes.oneOf(['default', 'first-run', 'header', 'sidebar']),
};
